import React from 'react';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import { 
  Shield, 
  AlertTriangle, 
  CheckCircle, 
  XCircle, 
  RotateCcw,
  ExternalLink
} from 'lucide-react';
import './ScamAnalysis.css';

const ScamAnalysis = ({ result, onReset, originalUrl }) => {
  const getScoreColor = (score) => {
    if (score <= 3) return '#10b981'; // Green - Safe
    if (score <= 6) return '#f59e0b'; // Yellow - Caution
    return '#ef4444'; // Red - High Risk
  };

  const getScoreLabel = (score) => {
    if (score <= 3) return 'Safe';
    if (score <= 6) return 'Caution';
    return 'High Risk';
  };

  const getScoreIcon = (score) => {
    if (score <= 3) return CheckCircle;
    if (score <= 6) return AlertTriangle;
    return XCircle;
  };

  const ScoreIcon = getScoreIcon(result.scamLikelihoodScore || 5);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="analysis-container"
    >
      <div className="analysis-header">
        <div className="score-display">
          <div className="score-circle" style={{ borderColor: getScoreColor(result.scamLikelihoodScore || 5) }}>
            <ScoreIcon 
              className="score-icon" 
              style={{ color: getScoreColor(result.scamLikelihoodScore || 5) }}
            />
            <div className="score-number" style={{ color: getScoreColor(result.scamLikelihoodScore || 5) }}>
              {result.scamLikelihoodScore || 'N/A'}
            </div>
          </div>
          <div className="score-info">
            <h2>Scam Likelihood Score</h2>
            <p className="score-label" style={{ color: getScoreColor(result.scamLikelihoodScore || 5) }}>
              {getScoreLabel(result.scamLikelihoodScore || 5)}
            </p>
            <p className="score-description">
              {result.scamLikelihoodScore <= 3 
                ? 'This website appears to be legitimate and safe.'
                : result.scamLikelihoodScore <= 6 
                ? 'This website shows some concerning patterns. Proceed with caution.'
                : 'This website shows multiple red flags. High risk of being a scam.'
              }
            </p>
          </div>
        </div>
        
        <div className="analyzed-url">
          <ExternalLink className="url-icon" />
          <span>{originalUrl}</span>
        </div>
      </div>

      {result.analysisReport && (
        <div className="analysis-details">
          <h3>Analysis Report</h3>
          <div className="report-content markdown-content">
            <ReactMarkdown>{result.analysisReport}</ReactMarkdown>
          </div>
        </div>
      )}

      {result.disclaimer && (
        <div className="disclaimer">
          <AlertTriangle className="disclaimer-icon" />
          <p>{result.disclaimer}</p>
        </div>
      )}

      <div className="analysis-actions">
        <motion.button
          onClick={onReset}
          className="reset-button"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <RotateCcw className="button-icon" />
          Analyze Another Website
        </motion.button>
      </div>
    </motion.div>
  );
};

export default ScamAnalysis;
